import { useState, useEffect, useCallback } from 'react';
import { isLandscape } from '@/utils/device';

/**
 * Reactive hook that returns whether the viewport is in the landscape phone
 * band (see {@link isLandscape}). Re-evaluates on resize / orientationchange.
 *
 * Why this exists separately from {@link useIsMobile}: layout code used to read
 * `isLandscape()` directly in render and relied on `useIsMobile()` to trigger
 * the re-render. But `useIsMobile` only changes (and thus only re-renders) when
 * `innerWidth` crosses 700. The landscape gate, however, can flip WITHOUT
 * `isMobile` changing — e.g. dragging the width from 700 → 701 keeps
 * `isMobile === false` (React bails out, no re-render) while `isLandscape()`
 * goes false → true. Components that branch on `isLandscape()` (notably the
 * PreferredPaymentView view selection and the Payment column class gating) then
 * stay stuck on the value committed at the boundary, showing the desktop layout
 * in a landscape viewport. Subscribing to landscape changes directly fixes that.
 */
export const useIsLandscape = (): boolean => {
  const [landscape, setLandscape] = useState(() => isLandscape());

  const handleChange = useCallback(() => {
    setLandscape(isLandscape());
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleChange);
    window.addEventListener('orientationchange', handleChange);

    // Some browsers fire orientationchange before the resize completes.
    const handleOrientationWithDelay = () => {
      setTimeout(handleChange, 100);
    };
    window.addEventListener('orientationchange', handleOrientationWithDelay);

    return () => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
      window.removeEventListener('orientationchange', handleOrientationWithDelay);
    };
  }, [handleChange]);

  return landscape;
};
